import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { SettingsService } from 'src/app/settings/settings.service';

/**
 * La cesión de facturas del portal sobre el stack nuevo (`/cliente/cesion-facturas/*`, F-P2).
 * Contrato: LemuBackend `docs/cesiones-facturas/api.md`, sección «Portal del cliente».
 *
 * Va con `HttpClient` y no con `SettingsService.get_query` a propósito: `get_query` cierra la
 * sesión ante cualquier 403 y abre el diálogo de error ante un 500. Acá un 403 sólo significa
 * «no eres titular de esta empresa» y un 409/5xx trae un `codigo` que la pantalla explica; nada de
 * eso tiene que sacar al cliente de su sesión.
 */

export type AccionPortal = 'buscar_dte' | 'subir_aec' | 'ceder_aec' | 'validar';

/**
 * Todas las fechas del panel de cesión: `dd/MM/yyyy HH:mm`, 24 horas, hora de Chile
 * (p. ej. `22/10/2026 15:51`), o sólo `dd/MM/yyyy` si en Chile son las 00:00. Un solo lugar
 * para el formato.
 *
 * No va con el pipe `date`: su `timezone` sólo acepta un offset fijo (`-0300`) y Chile cambia de
 * offset con el horario de verano; `Intl` con `America/Santiago` sí lo resuelve.
 */
export const FORMATO_FECHA_CESION = 'dd/MM/yyyy HH:mm';
export const ZONA_HORARIA_CESION = 'America/Santiago';

const _fechaCesion = new Intl.DateTimeFormat('es-CL', {
  timeZone: ZONA_HORARIA_CESION,
  day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});

/**
 * El backend manda ISO con offset (`2026-10-22T18:51:20+00:00`). Lo que no se puede leer como
 * fecha se muestra tal cual.
 */
export function formatearFechaCesion(fecha: string | null | undefined): string {
  if (!fecha) {
    return '';
  }
  const d = new Date(fecha);
  if (isNaN(d.getTime())) {
    return fecha;
  }
  const p: Record<string, string> = {};
  for (const parte of _fechaCesion.formatToParts(d)) {
    p[parte.type] = parte.value;
  }
  const dia = `${p['day']}/${p['month']}/${p['year']}`;
  // Algunas fechas del backend son sólo de día y llegan como medianoche de Chile
  // (`2026-10-19T03:00:00+00:00`): a las 00:00 justas se muestra sólo el día, como en clienteApp.
  if (p['hour'] === '00' && p['minute'] === '00') {
    return dia;
  }
  return `${dia} ${p['hour']}:${p['minute']}`;
}

export interface MensajeCesion {
  titulo: string | null;
  detalle: string | null;
}

export interface DocumentoCesion {
  documento_factura_id: number;
  folio: string;
  monto: string;
  rut_deudor: string;
  deudor: string;
  estado: string;
  estado_label: string;
  acciones: AccionPortal[];
  mensaje: MensajeCesion;
  marcas: { tiene_dte: boolean; tiene_aec: boolean; reclamada: boolean };
  aec: {
    nombre: string | null;
    origen: string | null;
    fecha_carga: string | null;
    errores: { codigo: string; mensaje: string }[];
  } | null;
  hitos: HitoCesion[];
}

/**
 * La línea de tiempo de la factura: siempre tres, en orden (`documentos`, `envio`, `resultado`).
 * La arma el backend en lenguaje de cliente; el portal no muestra el historial técnico.
 * `pendiente` es un paso que todavía no ocurrió: se muestra atenuado.
 */
export type EstadoHito = 'hecho' | 'en_curso' | 'error' | 'pendiente';

export interface HitoCesion {
  clave: 'documentos' | 'envio' | 'resultado';
  titulo: string;
  fecha: string | null;
  estado: EstadoHito;
}

export interface EstadoCesionPortal {
  resumen: { total: number; por_estado: { estado: string; etiqueta: string; cantidad: number }[] };
  metodos: { buscar_dte: boolean; facturador_nubox: boolean };
  credenciales: { tiene_certificado: boolean; tiene_clave_sii: boolean };
  documentos: DocumentoCesion[];
}

export interface Bloqueada {
  documento_id: number;
  folio: string;
  codigo: string;
  mensaje: string;
}

export interface ArchivoRechazado {
  nombre: string;
  folio: number | string | null;
  errores: { codigo: string; mensaje: string }[];
}

/** El cuerpo de `success`, sea la respuesta buena o mala: el backend siempre la envuelve así. */
export interface RespuestaPortal extends Partial<EstadoCesionPortal> {
  ok?: boolean;
  message?: string;
  codigo?: string | null;
  accion?: string;
  execution_id?: string;
  bloqueadas?: Bloqueada[];
  archivos_recibidos?: number;
  archivos_aceptados?: number;
  archivos_rechazados?: ArchivoRechazado[];
  habilitada?: boolean;
  data?: { password_valid?: boolean };
}

export interface Resultado {
  status: number;
  body: RespuestaPortal;
}

@Injectable({ providedIn: 'root' })
export class CesionPortalService {

  // `habilitada` se lee una vez cada tanto: el flag cambia con un reinicio del backend, no a
  // cada rato, y lo preguntan dos pantallas.
  private static readonly CACHE_HABILITADA_MS = 5 * 60 * 1000;
  private habilitadaCache: { valor: Promise<boolean>; hasta: number } | null = null;

  constructor(private http: HttpClient) {}

  private url(path: string): string {
    return `${SettingsService.API_ENDPOINT}${path}`;
  }

  private headers(): HttpHeaders {
    return new HttpHeaders().set('Authorization', 'JWT ' + (localStorage.getItem('token') || ''));
  }

  private async pedir(metodo: 'GET' | 'POST', path: string, body?: any): Promise<Resultado> {
    try {
      const opciones = { headers: this.headers(), observe: 'response' as const };
      const respuesta: any = metodo === 'GET'
        ? await firstValueFrom(this.http.get(this.url(path), opciones))
        : await firstValueFrom(this.http.post(this.url(path), body ?? {}, opciones));
      return { status: respuesta.status, body: respuesta.body?.success ?? {} };
    } catch (error: any) {
      if (error instanceof HttpErrorResponse) {
        const cuerpo: any = error.error;
        return { status: error.status, body: (cuerpo && typeof cuerpo === 'object' && cuerpo.success) || {} };
      }
      return { status: 0, body: {} };
    }
  }

  /**
   * ¿El portal usa el camino nuevo? Ante cualquier error, `false`: el portal sigue como siempre.
   */
  habilitada(): Promise<boolean> {
    const ahora = Date.now();
    if (this.habilitadaCache && this.habilitadaCache.hasta > ahora) {
      return this.habilitadaCache.valor;
    }
    const valor = this.pedir('GET', 'cliente/cesion-facturas/habilitada/')
      .then((r) => r.status === 200 && r.body.habilitada === true)
      .catch(() => false);
    this.habilitadaCache = { valor, hasta: ahora + CesionPortalService.CACHE_HABILITADA_MS };
    return valor;
  }

  /** El estado de cada factura. Concilia en el backend: es lo que se sondea. */
  estado(idSim: number | string): Promise<Resultado> {
    return this.pedir('GET', `cliente/cesion-facturas/${idSim}/`);
  }

  accion(idSim: number | string, accion: Exclude<AccionPortal, 'subir_aec'>, documentos?: number[]): Promise<Resultado> {
    const body: any = { accion };
    if (documentos && documentos.length) {
      body.documentos = documentos;
    }
    return this.pedir('POST', `cliente/cesion-facturas/${idSim}/acciones/`, body);
  }

  subirAec(idSim: number | string, archivos: File[]): Promise<Resultado> {
    const formData = new FormData();
    for (const archivo of archivos) {
      formData.append('files', archivo, archivo.name);
    }
    return this.pedir('POST', `cliente/cesion-facturas/${idSim}/aec/`, formData);
  }

  /**
   * Valida la clave tributaria de la empresa contra el SII y, si es correcta, la guarda
   * (`need_saved: true`): es la que usa «Ceder» (`ceder_aec`). Endpoint del portal de siempre,
   * sólo para el titular de la empresa (F-P0).
   */
  guardarClaveSii(idCompany: number | string, clave: string): Promise<Resultado> {
    return this.pedir('POST', `cliente/cesiones/${idCompany}/validate-login-company/`,
      { sii_pass: clave, need_saved: true });
  }
}
